import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  styled,
  TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useEffect, useState } from 'react';
import type { Expense, Chauffeur } from '../types';
import ExpenseDetailsDialog from '../components/ExpenseDetailsDialog';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    fontSize: '0.85rem',
  },
}));

const CustomTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
    fontSize: '0.9rem',
    padding: theme.spacing(1),
  },
}));

interface ExpensesListProps {
  expenses?: Expense[];
  chauffeurs: Chauffeur[];
  handleEditExpense: (expense: Expense) => void;
  handleDeleteExpense: (id: string) => void;
  handleDownloadDocument: (url: string) => void;
  currentPage: number;
  pageSize: number;
  totalExpenses: number;
  handlePageChange: (event: unknown, newPage: number) => void;
  handleRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ExpensesList = ({
  expenses = [],
  chauffeurs,
  handleEditExpense,
  handleDeleteExpense,
  handleDownloadDocument,
  currentPage,
  pageSize,
  totalExpenses,
  handlePageChange,
  handleRowsPerPageChange,
}: ExpensesListProps) => {
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  useEffect(() => {
    console.log('ExpensesList expenses:', expenses.map((e) => ({
      id: e.id,
      paymentMethod: e.paymentMethod,
      syncStatus: e.syncStatus,
      amount: e.amount,
      amountType: typeof e.amount,
    })));
  }, [expenses]);

  // Helper function to safely format amounts
  const formatAmount = (amount: string | number | undefined | null): string => {
    if (amount === undefined || amount === null || amount === '') return '0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return isNaN(numAmount) ? '0.00' : numAmount.toFixed(2);
  };

  // Helper function to safely format quantity
  const formatQuantity = (quantity: string | number | undefined | null): string => {
    if (quantity === undefined || quantity === null || quantity === '') return '0';
    const numQuantity = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
    return isNaN(numQuantity) ? '0' : numQuantity.toString();
  };

  const getSyncStatusChip = (syncStatus: string) => {
    if (!syncStatus) return { label: 'N/A', color: 'default' };
    
    switch (syncStatus.toLowerCase()) {
      case 'synced':
      case 'synchronise':
        return { label: 'Synchronisé', color: 'success' };
      case 'pending':
      case 'en_attente':
        return { label: 'En attente', color: 'warning' };
      case 'error':
      case 'erreur':
        return { label: 'Erreur', color: 'error' };
      default:
        return { label: syncStatus, color: 'default' };
    }
  };

  const getPaymentMethodDisplay = (paymentMethod: string | undefined): string => {
    if (!paymentMethod) return 'N/A';
    
    const methods: Record<string, string> = {
      'cash': 'Espèces',
      'especes': 'Espèces',
      'card': 'Carte',
      'carte': 'Carte',
      'transfer': 'Virement',
      'virement': 'Virement',
      'check': 'Chèque',
      'cheque': 'Chèque',
    };
    
    return methods[paymentMethod.toLowerCase()] || 
           (paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1));
  };

  const getExpenseTypeDisplay = (type: string | undefined): string => {
    if (!type) return 'N/A';
    const types: Record<string, string> = {
      fuel: 'Carburant',
      maintenance: 'Entretien',
      repair: 'Réparations',
      welding: 'Soudure',
      wash: 'Lavage',
      tire: 'Pneus / Pièces',
      battery: 'Batteries',
      adblue: 'Ad Blue',
      toll: 'Péage',
      driver_pay: 'Paiement Chauffeur',
      other: 'Autre',
    };
    const key = type.toLowerCase();
    return types[key] || (type.charAt(0).toUpperCase() + type.slice(1));
  };

  const monthNameFR = (month?: number | null) => {
    if (!month) return '';
    const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
    return months[Math.min(12, Math.max(1, month)) - 1] || '';
  };

  const handleShowDetails = (expense: Expense) => {
    setSelectedExpense(expense);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedExpense(null);
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      >
        <Table aria-label="Tableau des dépenses">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <StyledTableCell>
                <strong>Chauffeur</strong>
              </StyledTableCell>
              <StyledTableCell>
                <strong>Type</strong>
              </StyledTableCell>
              <StyledTableCell>
                <strong>Période</strong>
              </StyledTableCell>
              <StyledTableCell>
                <strong>Montant (FCFA)</strong>
              </StyledTableCell>
              <StyledTableCell>
                <strong>Date</strong>
              </StyledTableCell>
              <StyledTableCell>
                <strong>Mode de paiement</strong>
              </StyledTableCell>
              <StyledTableCell>
                <strong>Statut Sync</strong>
              </StyledTableCell>
              <StyledTableCell align="right">
                <strong>Actions</strong>
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={7} align="center">
                  Aucune dépense disponible.
                </StyledTableCell>
              </TableRow>
            ) : (
              expenses.map((expense) => {
                const syncStatusChip = getSyncStatusChip(expense.syncStatus || 'N/A');
                
                return (
                  <StyledTableRow key={expense.id}>
                    <StyledTableCell>
                      {expense.driverName || 'Non assigné'}
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      {getExpenseTypeDisplay(expense.type)}
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      {expense.type === 'driver_pay' && (expense.periodMonth || expense.periodYear)
                        ? `${monthNameFR(expense.periodMonth)} ${expense.periodYear || ''}`.trim()
                        : ''}
                    </StyledTableCell>

                    <StyledTableCell>
                      {formatAmount(expense.amount)}
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      {expense.date
                        ? new Date(expense.date).toLocaleDateString('fr-FR')
                        : 'N/A'}
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      {getPaymentMethodDisplay(expense.paymentMethod)}
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      <Chip
                        label={syncStatusChip.label}
                        color={
                          syncStatusChip.color as
                            | 'success'
                            | 'warning'
                            | 'error'
                            | 'default'
                        }
                        size="small"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </StyledTableCell>
                    
                    <StyledTableCell align="right">
                      <CustomTooltip title="Voir les détails">
                        <IconButton
                          aria-label="Voir les détails"
                          onClick={() => handleShowDetails(expense)}
                          sx={{
                            color: 'info.main',
                            '&:hover': { color: 'info.dark' },
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </CustomTooltip>
                      
                      <CustomTooltip title="Modifier la dépense">
                        <IconButton
                          aria-label="Modifier la dépense"
                          onClick={() => handleEditExpense(expense)}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { color: 'primary.dark' },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </CustomTooltip>
                      
                      <CustomTooltip title="Supprimer la dépense">
                        <IconButton
                          aria-label="Supprimer la dépense"
                          onClick={() => handleDeleteExpense(expense.id)}
                          sx={{
                            color: 'error.main',
                            '&:hover': { color: 'error.dark' },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </CustomTooltip>
                      
                      {expense.imageUrl && (
                        <CustomTooltip title="Télécharger le reçu">
                          <IconButton
                            aria-label="Télécharger le reçu"
                            onClick={() =>
                              handleDownloadDocument(expense.imageUrl!)
                            }
                            sx={{
                              color: 'secondary.main',
                              '&:hover': { color: 'secondary.dark' },
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </CustomTooltip>
                      )}
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalExpenses}
          rowsPerPage={pageSize}
          page={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Dépenses par page :"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} sur ${count}`
          }
        />
      </TableContainer>
      
      <ExpenseDetailsDialog
        open={openDetails}
        expense={selectedExpense}
        onClose={handleCloseDetails}
      />
    </>
  );
};

export default ExpensesList;